import type {
  ColumnSpec,
  ColumnType,
  HostApi,
  ImporterSpec,
  ImportSourceInput,
  PluginModule,
  Table,
  TableInfo,
  TableOrigin,
  TableSource,
  ViewInstance,
  ViewTemplate,
  WindowGeometry,
} from '@easydb/shared';
import type { ColumnMapping } from '../import/map-columns.js';
import { chooseTables } from '../dialogs/table-select-dialog.js';
import { askImportOntoMode, columnsLineUp } from '../import/import-mode.js';
import { quoteBigIntegers } from '../import/big-numbers.js';
import { runImport } from '../import/import-kernel.js';
import { inferColumnType } from '../import/infer-type.js';
import { remapRows } from '../table/column-merge.js';
import { filenameFromUrl } from '../import/fetch-source.js';
import { cryptoUUID, slugTable } from '../util/ids.js';
import { restoreTemplates } from '../views/template-restore.js';
// Type-only: erased at compile time, so importing this module for its type
// never pulls in `lit`/`top-progress.js` at runtime (that module registers a
// custom element on import, which would blow up under Vitest's default
// Node environment). The actual class is loaded lazily via dynamic import()
// only on the large-import path below, once we're past unit-test territory.
import type { ProgressHandle } from '../chrome/top-progress.js';

export const meta: NonNullable<PluginModule['meta']> = {
  id: 'json-import',
  name: 'JSON Import',
  type: 'importer',
  version: '0.1.0',
  description: 'Drag-and-drop JSON to create tables. Supports native dumps and arrays of objects.',
  author: 'Marc Cawood',
  icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M8 3a2 2 0 0 0-2 2v4a2 2 0 0 1-2 2 2 2 0 0 1 2 2v4a2 2 0 0 0 2 2"/><path d="M16 3a2 2 0 0 1 2 2v4a2 2 0 0 0 2 2 2 2 0 0 0-2 2v4a2 2 0 0 1-2 2"/></svg>',
  repo: 'https://github.com/cawoodm/easydbaccess/blob/main/packages/renderer/src/plugins/json-import.ts',
};

export function init(api: HostApi): void {
  api.ui.registerImporter(importerSpec);
  api.ui.registerDropHandler(async (event) => {
    const files = filesFrom(event);
    const jsons = files.filter(isJson);
    if (jsons.length === 0) return false;

    event.preventDefault();
    for (const file of jsons) {
      // A drop ON a table window names its destination — the same question a
      // CSV drop asks. Anything else is an ordinary import.
      if (await dropOntoTable(api, event, file)) continue;
      await importJsonFile(api, file);
    }
    return true;
  });
}

/**
 * A `.json` / `.table.json` dropped onto a table's window: that table is the
 * destination, so ask what should happen to it and do it. Returns false when the
 * drop landed anywhere else, or when the user chose "a new table", leaving the
 * ordinary import to run.
 */
async function dropOntoTable(api: HostApi, event: DragEvent, file: File): Promise<boolean> {
  const { tableIdAtNode } = await import('../window-mgr/table-window-manager.js');
  const tableId = tableIdAtNode(event.target);
  if (!tableId) return false;
  const table = await api.store.tables.findOne(tableId);
  if (!table) return false;
  // A read-only table (a live connection, a reference) cannot take rows at all.
  if (table.readonly === true || table.source != null) {
    api.ui.dialogs.toast(`"${table.name}" is read-only, so the file was imported as a new table.`, { kind: 'warning', title: 'Import JSON' });
    return false;
  }

  const text = await file.text();
  let parsed: unknown;
  try {
    parsed = JSON.parse(quoteBigIntegers(text));
  } catch {
    return false; // let the ordinary import report the parse error
  }
  const incoming = parsedToTables(parsed, stripJsonExt(file.name));
  if (incoming.length === 0) return false;

  const source = await pickSourceTable(api, incoming, table.name);
  if (!source) return true; // the picker was dismissed ⇒ cancelled, and it was ours

  const mode = await askImportOntoMode(api, { fileName: file.name, tableName: table.name, title: 'Import JSON' });
  if (!mode) return true; // dismissed ⇒ the drop is cancelled, and it was ours
  if (mode === 'new') return false;
  await importIntoTable(api, table, source, mode);
  return true;
}

/**
 * Which table in the FILE is being loaded into the window it was dropped on.
 *
 * A drop on a window says where the data is going and not what part of the file it
 * is, so a file holding several tables leaves one question open. Answered in the
 * order a person would: a table of the same name is obviously the one meant, and only
 * a file with no such table has to ask.
 *
 * This used to refuse — "holds 5 tables, drop it outside a window" — which told the
 * user their aim was wrong when it was the only unambiguous part of what they did.
 *
 * Returns null only when the user dismissed the picker. A single-table file never
 * asks: there is nothing to choose, whatever it is called.
 */
async function pickSourceTable(api: HostApi, incoming: NormalizedTable[], targetName: string): Promise<NormalizedTable | null> {
  if (incoming.length === 1) return incoming[0]!;
  const target = targetName.trim().toLowerCase();
  const byName = incoming.find((t) => t.name.trim().toLowerCase() === target);
  if (byName) return byName;

  // Labelled with the row count, so two tables of the same name in one file are
  // still tellable apart — a choice dialog reports back the label it was given.
  const labels = incoming.map((t) => `${t.name} — ${t.rows.length.toLocaleString()} row${t.rows.length === 1 ? '' : 's'}`);
  const pick = await api.ui.dialogs.choice(`"${targetName}" is the destination. Which table from the file should be loaded into it?`, labels, 'Import JSON');
  if (!pick) return null;
  const at = labels.indexOf(pick);
  return at >= 0 ? incoming[at]! : null;
}

/**
 * Write ONE incoming table into an existing table, the three ways that are not
 * "make another table".
 *
 *  - `recreate` — the file's columns replace the table's, and its rows replace
 *    the data. The table keeps its id, name and window, so a projection or view
 *    bound to it survives.
 *  - `reload` / `append` — the table's columns stay and the incoming FIELDS are
 *    mapped onto them: silently when they line up (a file the table came from),
 *    through the column mapper when they do not. `reload` wipes the rows first.
 *
 * JSON values are already typed, so nothing is coerced on the way in — unlike a
 * CSV, where every cell arrives as text.
 */
async function importIntoTable(api: HostApi, table: Table, incoming: NormalizedTable, mode: 'recreate' | 'reload' | 'append'): Promise<void> {
  const rowColl = api.store.rows(table.id);
  api.events.emit('import:before', { source: 'json', tableId: table.id });

  let rows: Array<Record<string, unknown>>;
  if (mode === 'recreate') {
    await api.store.tables.patch(table.id, { columns: incoming.columns, updatedAt: Date.now() });
    rows = incoming.rows;
  } else {
    const fields = incoming.columns.map((c) => c.field);
    let mapping: ColumnMapping = table.columns.map((c: ColumnSpec) => c.field);
    if (!columnsLineUp(fields, table.columns)) {
      const { mapColumnsToTable } = await import('../dialogs/column-map-dialog.js');
      const sample = fields.map((f) => String(incoming.rows[0]?.[f] ?? ''));
      const chosen = await mapColumnsToTable(fields, table.columns, table.name, sample);
      if (chosen === null) return; // the mapper was cancelled
      mapping = chosen;
    }
    rows = incoming.rows.map((row) => {
      const out: Record<string, unknown> = {};
      fields.forEach((field, i) => {
        const target = mapping[i];
        if (target) out[target] = row[field];
      });
      return out;
    });
  }

  if (mode !== 'append') {
    const old = await rowColl.find();
    await rowColl.bulkRemove(old.map((r) => r.id));
  }
  const docs = rows.map((data) => ({ id: cryptoUUID(), tableId: table.id, data, updatedAt: Date.now() }));
  await rowColl.bulkInsert(docs);
  api.events.emit('import:after', { source: 'json', tableId: table.id, rowCount: docs.length });
}

// -- Importer spec ------------------------------------------------------------
//
// This spec is the GENERIC JSON importer: an array of objects, a `{rows: […]}`
// envelope, or a single object. Those are plain tabular data, so they run on
// the import kernel exactly like a CSV.
//
// A native `.db.json` dump is a different thing. It carries window geometry,
// sort, filters, view templates and view instances, and can replace the whole
// workspace — that is RESTORING a workspace, not importing a table, and no
// generic table writer can express it. It keeps its own path
// (`restoreWorkspaceDump` below). `isWorkspaceDump` is how a caller tells them
// apart before choosing which to run.
//
// `list` parses once and returns one candidate per table, carrying the parsed
// table as the opaque handle. `read` hands back what `list` already produced —
// no second parse.

/**
 * True when this parsed body should route to the restore path
 * ({@link restoreWorkspaceDump}) rather than the import kernel: a full
 * workspace dump (native `{ tables: [...] }` or v1), or a single native table
 * (the per-table JSON export). Only the restore path can carry `origin`,
 * `windowGeometry`, `info`, sort/filters/labelColumn/deletedColumns — the
 * kernel physically cannot. Exported so the Import dialog and the drop
 * handler can tell dumps apart from plain tabular JSON.
 */
export function isWorkspaceDump(parsed: unknown): boolean {
  if (!isObject(parsed)) return false;
  if (looksLikeV1Dump(parsed as Record<string, unknown>)) return true;
  if (Array.isArray((parsed as { tables?: unknown }).tables)) return true;
  return isSingleNativeTable(parsed);
}

/** The name a source should give its table, before the kernel's naming policy. */
function candidateName(input: ImportSourceInput): string {
  if (input.kind === 'file' && input.file) return stripJsonExt(input.file.name);
  if (input.kind === 'url' && input.url) return stripJsonExt(filenameFromUrl(input.url));
  return stripJsonExt(input.name ?? 'imported');
}

const importerSpec: ImporterSpec = {
  id: 'json',
  label: 'JSON (array of objects or a dump)',
  icon: 'data_object',
  order: 20,
  accept: ['.json', '.db.json', 'application/json'],
  samples: [
    {
      label: 'Northwind — sample database (JSON dump)',
      url: 'https://raw.githubusercontent.com/cawoodm/easydbaccess/main/data/northwind.db.json',
    },
  ],
  supports: { url: true, file: true, text: true, reference: true, multiTable: true, kernel: true },

  detect(input) {
    const name = input.kind === 'file' ? (input.file?.name ?? '') : (input.url ?? '');
    if (/\.db\.json$/i.test(name)) return 1; // our own dump format
    if (/\.json$/i.test(name)) return 0.95;
    if (input.file?.type === 'application/json') return 0.9;
    const body = (input.text ?? '').trimStart();
    return body.startsWith('{') || body.startsWith('[') ? 0.3 : 0;
  },

  async list(ctx, input) {
    let text: string;
    if (input.kind === 'file' && input.file) text = await input.file.text();
    else if (input.kind === 'url' && input.url) {
      text = await ctx.fetchText(input.url, `Reading ${filenameFromUrl(input.url)}…`);
    } else text = input.text ?? '';

    const fallback = candidateName(input);

    let parsed: unknown;
    try {
      parsed = JSON.parse(quoteBigIntegers(text));
    } catch (err) {
      throw new Error(`Invalid JSON in ${fallback}: ${(err as Error).message}`, {
        cause: err,
      });
    }
    const tables = parsedToTables(parsed, fallback);
    return tables.map((t) => ({
      name: t.name,
      rowCount: t.rows.length,
      handle: { table: t, input, single: tables.length === 1 } satisfies JsonHandle,
    }));
  },

  async *read(_ctx, candidate) {
    const { table } = candidate.handle as JsonHandle;
    yield { columns: table.columns, rows: table.rows };
  },

  reference(_ctx, candidate) {
    const { input, single } = candidate.handle as JsonHandle;
    if (input.kind !== 'url' || !input.url) {
      throw new Error('A reference needs a re-fetchable URL — an upload cannot be referenced.');
    }
    // The `url` row-source re-fetches the WHOLE document and reads the first
    // array of objects out of it. That is only the right table when the
    // document held exactly one, so refuse a multi-table dump rather than
    // silently referencing the wrong rows.
    if (!single) {
      throw new Error('That URL holds several tables, so a reference would be ambiguous. Import a copy instead.');
    }
    return { type: 'url', config: { url: input.url, format: 'json' } };
  },
};

/** What json-import passes to itself between `list` and `read`/`reference`. */
interface JsonHandle {
  table: NormalizedTable;
  input: ImportSourceInput;
  /** The document held exactly one table, so a `url` reference is unambiguous. */
  single: boolean;
}

/** Strip `.db.json` / `.json` so a dump names its table without the extension. */
function stripJsonExt(name: string): string {
  return name.replace(/\.db\.json$/i, '').replace(/\.json$/i, '') || 'imported';
}

// -- Core: file -> Tables -----------------------------------------------------

/**
 * A dropped `.json` file. A workspace dump is restored whole; anything else is
 * plain tabular data and goes through the kernel, so a dropped file lands
 * exactly like the same file chosen in the Import dialog.
 */
async function importJsonFile(api: HostApi, file: File): Promise<void> {
  const text = await file.text();
  let parsed: unknown;
  try {
    parsed = JSON.parse(quoteBigIntegers(text));
  } catch (err) {
    api.events.emit('plugin:error', {
      url: 'json-import',
      phase: 'runtime',
      error: new Error(`Invalid JSON in ${file.name}: ${(err as Error).message}`),
    });
    return;
  }

  if (isWorkspaceDump(parsed)) {
    await restoreWorkspaceDump(api, text, file.name);
    return;
  }

  const res = await runImport(api, importerSpec, { kind: 'text', text, name: file.name }, { mode: 'copy', target: { kind: 'new' } });
  const rows = res.landed.reduce((n, l) => n + l.rowCount, 0);
  if (res.landed.length > 0) {
    api.ui.dialogs.toast(`Imported ${file.name} (${rows.toLocaleString()} rows).`, {
      kind: 'success',
      title: 'Import',
    });
  }
}

/**
 * Restore a native (or v1) workspace dump: tables plus everything around them
 * — window geometry, sort, filters, label column, deleted columns, view
 * templates and view instances — with the option to replace the whole
 * workspace. This is deliberately NOT the import kernel's job: the kernel
 * writes name, columns, rows and origin, which is all a table import means.
 * Plain tabular JSON goes through the kernel instead (see `importerSpec`).
 *
 * Reached from the Import dialog and the drop handler once
 * {@link isWorkspaceDump} says the body is a dump.
 */
export async function restoreWorkspaceDump(
  api: HostApi,
  text: string,
  filename: string,
  opts: {
    originUrl?: string | undefined;
    maxRows?: number | undefined;
    /**
     * Hook for the Import dialog's "Edit columns before import" checkbox.
     * Called once per NEW table with that table's inferred columns and its
     * name. Return the edited columns, or `null` to leave that one table out.
     * Matched tables in overwrite mode reuse their existing schema, so they
     * never open the editor — the same rule the CSV importer follows.
     */
    editColumns?: ((columns: ColumnSpec[], tableName: string) => Promise<ColumnSpec[] | null>) | undefined;
  } = {},
): Promise<void> {
  const workspaceId = api.workspaceId();
  if (!workspaceId) throw new Error('json-import: no active workspace');

  let parsed: unknown;
  try {
    parsed = JSON.parse(quoteBigIntegers(text));
  } catch (err) {
    api.events.emit('plugin:error', {
      url: 'json-import',
      phase: 'runtime',
      error: new Error(`Invalid JSON in ${filename}: ${(err as Error).message}`),
    });
    return;
  }

  const baseName = filename.replace(/\.db\.json$/i, '').replace(/\.json$/i, '') || 'imported';
  const allTables = parsedToTables(parsed, baseName);
  if (allTables.length === 0) return;

  // Multi-table dumps: let the user pick which tables to import (all selected
  // by default). Single-table dumps skip the picker.
  let tables = allTables;
  if (allTables.length > 1) {
    const picked = await chooseTables(
      allTables.map((t) => ({ name: t.name, size: t.rows.length })),
      {
        title: 'Import tables',
        message: `"${filename}" contains ${allTables.length} tables. Choose which to import.`,
        confirmLabel: 'Import',
      },
    );
    if (!picked) return; // cancelled
    tables = picked.map((i) => allTables[i]!);
  }

  // If any imported table name overlaps an existing one OR this is clearly a
  // multi-table dump, ask the user how to resolve. Single-table imports with
  // no name collision skip the prompt.
  const existing = (await api.store.tables.find()).filter((t) => t.workspaceId === workspaceId);
  const incomingNames = new Set(tables.map((t) => t.name));
  const collisions = existing.filter((t) => incomingNames.has(t.name));

  let mode: 'overwrite-matching' | 'replace-workspace' | 'append-new';
  if (collisions.length === 0 && tables.length === 1) {
    mode = 'append-new';
  } else if (tables.length === 1 && collisions.length === 1) {
    // ONE table naming ONE existing table is not a workspace question at all —
    // it is the same question a drop on that table's window asks, so it gets the
    // same four answers. "Replace entire workspace" for a single-table file was
    // a trap: it deleted every OTHER table too.
    const target = collisions[0]!;
    const picked = await askImportOntoMode(api, {
      fileName: filename,
      tableName: target.name,
      title: 'JSON import',
      reason: `A table named "${target.name}" already exists in this workspace.`,
    });
    if (!picked) return; // cancelled
    if (picked === 'reload' || picked === 'append') {
      // Rows into the table that is already there; its columns, geometry, sort
      // and views all stay as they are. Nothing else in the dump applies.
      await importIntoTable(api, target, tables[0]!, picked);
      return;
    }
    mode = picked === 'recreate' ? 'overwrite-matching' : 'append-new';
  } else {
    const opts = collisions.length > 0 ? [`Overwrite matching (${collisions.length})`, 'Replace entire workspace', 'Add as new tables'] : ['Add to current workspace', 'Replace entire workspace'];
    const choice = await api.ui.dialogs.choice(
      `Importing ${tables.length} table${tables.length === 1 ? '' : 's'} from "${filename}".${
        collisions.length > 0 ? `\n\n${collisions.length} table${collisions.length === 1 ? '' : 's'} share a name with existing data.` : ''
      }`,
      opts,
      'JSON import',
    );
    if (!choice) return; // cancelled
    if (choice.startsWith('Overwrite matching')) mode = 'overwrite-matching';
    else if (choice === 'Replace entire workspace') mode = 'replace-workspace';
    else mode = 'append-new';
  }

  // The slow phase for large dumps (e.g. Northwind's ~10s) is what's left:
  // optional replace-workspace cleanup, then the per-table bulkInsert loop.
  // Gate the top progress bar on total row count rather than a timer — unlike
  // a network fetch we have no "in progress, taking a while" signal to hook a
  // slow-threshold off before the work is already done, but we do know the
  // row counts up front, and they're a fair proxy for how long bulkInsert
  // will take. Small imports skip the bar entirely rather than flashing it.
  const ROW_THRESHOLD = 2000;
  const totalRows = tables.reduce((sum, t) => sum + (t.source ? 0 : Math.min(t.rows.length, opts.maxRows ?? Infinity)), 0);
  let handle: ProgressHandle | null = null;
  if (totalRows >= ROW_THRESHOLD) {
    const { TopProgress } = await import('../chrome/top-progress.js');
    handle = TopProgress.begin(`Importing ${filename}…`);
  }

  try {
    if (mode === 'replace-workspace') {
      for (const t of existing) {
        const rowColl = api.store.rows(t.id);
        const rs = await rowColl.find();
        await rowColl.bulkRemove(rs.map((r) => r.id));
        await api.store.tables.remove(t.id);
      }
    }

    const existingByName = new Map(existing.map((t) => [t.name, t] as const));
    // Maps each imported table's name → its (new or matched) id, so view
    // instances in the dump can be re-pointed at the freshly-minted table ids.
    const nameToId = new Map<string, string>();
    /** Tables the store had to rename to keep names unique: dump name → stored name. */
    const renamed: Array<[string, string]> = [];
    let rowsDone = 0;
    for (const t of tables) {
      // Prefer backing info embedded in the dump (another device's export); else,
      // if we fetched this dump from a URL, record a snapshot origin so the table
      // can be refreshed/reloaded later.
      const source = t.source;
      const origin = t.origin ?? (!source && opts.originUrl ? ({ type: 'json', url: opts.originUrl } as TableOrigin) : undefined);

      let tableId: string;
      const match = mode === 'overwrite-matching' ? existingByName.get(t.name) : undefined;

      // "Edit columns before import": review/rename this table's columns before
      // it is created. Only for a brand-new LOCAL table — a matched table keeps
      // its own schema, and a live (`source`) table's columns belong to the
      // remote. Cancelling leaves this one table out and continues with the rest.
      let cols = t.columns;
      let rows = t.rows;
      if (opts.editColumns && !match && !source) {
        const edited = await opts.editColumns(cols, t.name);
        if (edited === null) continue;
        rows = remapRows(rows, cols, edited);
        cols = edited;
      }

      if (match) {
        // Overwrite: keep the id (and thus its panel position) but wipe rows
        // and replace columns + sort + geometry + backing info from the import.
        tableId = match.id;
        // Only clear LOCAL rows. A matched table that's already live (`source`)
        // routes rows to its remote provider — we must not issue remote deletes.
        if (!match.source) {
          const rowColl = api.store.rows(tableId);
          const oldRows = await rowColl.find();
          await rowColl.bulkRemove(oldRows.map((r) => r.id));
        }
        await api.store.tables.patch(tableId, {
          columns: cols,
          ...(t.title ? { title: t.title } : {}),
          ...(t.windowGeometry ? { windowGeometry: t.windowGeometry } : {}),
          ...(t.sortColumn ? { sortColumn: t.sortColumn, sortAsc: t.sortAsc ?? true } : { sortColumn: undefined, sortAsc: undefined }),
          ...(t.filters ? { filters: t.filters } : {}),
          ...(t.labelColumn ? { labelColumn: t.labelColumn } : {}),
          ...(t.info ? { info: t.info } : {}),
          ...(t.deletedColumns ? { deletedColumns: t.deletedColumns } : {}),
          ...(t.readonly ? { readonly: true } : {}),
          source: source ?? undefined,
          origin: origin ?? undefined,
          updatedAt: Date.now(),
        });
      } else {
        tableId = cryptoUUID();
        api.events.emit('import:before', { source: 'json', tableId });
        // "Add as new tables" on a dump whose names are already taken: the store
        // uniques the name (`orders` → `orders-2`) rather than making a second
        // `orders`, and hands back what it stored. Say so, or the new table
        // looks like it silently overwrote the old one.
        const stored = await api.store.tables.insert({
          id: tableId,
          workspaceId,
          name: t.name,
          code: slugTable(t.name),
          columns: cols,
          view: 'table',
          ...(t.title ? { title: t.title } : {}),
          ...(t.windowGeometry ? { windowGeometry: t.windowGeometry } : {}),
          ...(t.sortColumn ? { sortColumn: t.sortColumn, sortAsc: t.sortAsc ?? true } : {}),
          ...(t.filters ? { filters: t.filters } : {}),
          ...(t.labelColumn ? { labelColumn: t.labelColumn } : {}),
          ...(t.info ? { info: t.info } : {}),
          ...(t.deletedColumns ? { deletedColumns: t.deletedColumns } : {}),
          ...(t.readonly ? { readonly: true } : {}),
          ...(source ? { source } : {}),
          ...(origin ? { origin } : {}),
          updatedAt: Date.now(),
        });
        if (stored.name !== t.name) renamed.push([t.name, stored.name]);
      }
      // Keyed by the name the DUMP used — that is what its view instances name.
      nameToId.set(t.name, tableId);

      // Snapshot rows are stored locally; a live (`source`) table pulls its own
      // rows from the provider, so we do NOT insert them here (that would write
      // to the remote). Its routed collection loads them on render.
      let inserted = 0;
      if (!source) {
        const rowColl = api.store.rows(tableId);
        // Apply the Import dialog's "Limit rows" cap per table (undefined ⇒ all).
        const rowsToInsert = opts.maxRows != null ? rows.slice(0, opts.maxRows) : rows;
        const docs = rowsToInsert.map((row) => ({
          id: cryptoUUID(),
          tableId,
          data: row,
          updatedAt: Date.now(),
        }));
        await rowColl.bulkInsert(docs);
        inserted = docs.length;
        rowsDone += inserted;
        // Weight progress by row count rather than table count, so a dump with
        // one huge table and several tiny ones doesn't jump straight to 90%.
        handle?.fraction(totalRows > 0 ? rowsDone / totalRows : 1);
      }

      api.events.emit('import:after', { source: 'json', tableId, rowCount: inserted });
    }

    // Restore view templates (workspace-global) and view instances (per-table),
    // re-pointing each instance at the freshly-imported table id by name. Only
    // native dumps carry these; other JSON shapes leave them undefined.
    await restoreViews(api, parsed, workspaceId, nameToId, mode === 'replace-workspace');

    if (renamed.length > 0) {
      api.ui.dialogs.toast(renamed.map(([from, to]) => `“${from}” came in as “${to}”`).join('\n'), { kind: 'info', title: 'Names must be unique' });
    }
  } finally {
    handle?.done();
  }

  // Windows were opened one-per-insert (liveQuery fires per write), so their
  // saved z-order / geometry wasn't applied as a batch. Ask the window manager
  // to restack by saved z — mirrors the gist pull path.
  document.dispatchEvent(new CustomEvent('easydb:restack-windows'));
}

/**
 * Re-creates `ViewTemplate`s and `ViewInstance`s carried in a native dump.
 * Templates are merged by name, never cleared — see `restoreTemplates`. In
 * replace-workspace mode the old instances are cleared first (their tables were
 * just wiped); otherwise instances are upserted. Each instance's `tableId` is
 * remapped via `nameToId` (falling back to its stored id) so it binds to the
 * table that was actually imported, its `templateId` via the remap
 * `restoreTemplates` returns, and its `dock` host through {@link remapDock}.
 */
async function restoreViews(api: HostApi, parsed: unknown, workspaceId: string, nameToId: Map<string, string>, replaceWorkspace: boolean): Promise<void> {
  if (!isObject(parsed)) return;
  const p = parsed as { viewTemplates?: unknown; viewInstances?: unknown };
  const templates = Array.isArray(p.viewTemplates) ? (p.viewTemplates as ViewTemplate[]) : [];
  const instances = Array.isArray(p.viewInstances) ? (p.viewInstances as ViewInstance[]) : [];
  if (templates.length === 0 && instances.length === 0) return;

  if (replaceWorkspace) {
    const stale = (await api.store.viewInstances.find()).filter((v) => v.workspaceId === workspaceId);
    await api.store.viewInstances.bulkRemove(stale.map((v) => v.id));
  }

  const templateIds = await restoreTemplates(api.store.viewTemplates, workspaceId, templates);

  for (const inst of instances) {
    if (!isObject(inst) || typeof inst.id !== 'string') continue;
    const tableId = (inst.tableName ? nameToId.get(inst.tableName) : undefined) ?? inst.tableId;
    if (!tableId) continue;
    const templateId = templateIds.get(inst.templateId) ?? inst.templateId;
    const dock = remapDock(inst.dock, inst.tableId, tableId);
    await api.store.viewInstances.upsert({ ...inst, workspaceId, tableId, templateId, ...(dock === undefined ? { dock: undefined } : { dock }) });
  }
}

/**
 * Carry a docked visualization's HOST across the import's re-identification.
 *
 * A `ViewDock` names its host by id, and an imported table gets a NEW id — so a
 * dock left as written points at a table that does not exist, and a pane whose
 * host stack never appears is not mounted at all. It does not fall back to a
 * window either (the window reconcile deliberately skips docked instances), so
 * the chart simply vanishes. That is what a dump of a workspace with docked
 * panes did before this.
 *
 * The case that resolves is the ordinary one: the pane is docked into the window
 * of the very table it charts, so the old host id equals the instance's old
 * `tableId` and the new host is the new one. A host that is some OTHER table (a
 * chart of a projection docked into the raw table's window) cannot be resolved —
 * the dump carries no table ids to match it against — so the dock is DROPPED and
 * the instance opens in its own window. Visible in the wrong place beats
 * invisible.
 *
 * A `view` host keeps its id: instance ids survive the import unchanged.
 */
function remapDock(dock: ViewInstance['dock'], oldTableId: string, newTableId: string): ViewInstance['dock'] {
  if (!dock) return undefined;
  if (dock.host.kind === 'view') return dock;
  if (dock.host.tableId === newTableId) return dock;
  if (dock.host.tableId === oldTableId) return { ...dock, host: { kind: 'table', tableId: newTableId } };
  return undefined;
}

// -- Shape detection ----------------------------------------------------------

interface NormalizedTable {
  name: string;
  columns: ColumnSpec[];
  rows: Array<Record<string, unknown>>;
  title?: string;
  windowGeometry?: WindowGeometry;
  sortColumn?: string;
  sortAsc?: boolean;
  filters?: Record<string, string>;
  labelColumn?: string;
  info?: TableInfo;
  deletedColumns?: string[];
  readonly?: boolean;
  /** Live remote backing carried in the dump (rows re-pulled from the provider). */
  source?: TableSource;
  /** Snapshot origin URL carried in the dump (refreshable). */
  origin?: TableOrigin;
}

/** Recognize known shapes; fall back to single-row import for unknown objects. */
export function parsedToTables(parsed: unknown, fallbackName: string): NormalizedTable[] {
  // Shape: { "<Name>.table.json": { dataArray, columns, elementRect, ... } }
  // — minniDBMax v1 / legacy dump format. Detected first because it overlaps
  // structurally with the generic-object fallback below.
  if (isObject(parsed) && looksLikeV1Dump(parsed as Record<string, unknown>)) {
    return convertV1Dump(parsed as Record<string, unknown>);
  }

  // Shape: { tables: [{ name, columns, rows }, ...] }  — native dump.
  // Entries may also be v1-shaped wrappers ({ "<name>.table.json": { dataArray, columns } })
  // — happens when a v1 file was Dumped through us before v1 detection landed.
  if (isObject(parsed) && Array.isArray((parsed as { tables?: unknown }).tables)) {
    const dump = parsed as { tables: unknown[] };
    const out: NormalizedTable[] = [];
    for (const entry of dump.tables) {
      if (isNativeTable(entry)) {
        out.push(nativeTableToNormalized(entry));
        continue;
      }
      if (isObject(entry) && looksLikeV1Dump(entry as Record<string, unknown>)) {
        out.push(...convertV1Dump(entry as Record<string, unknown>));
      }
    }
    return out;
  }

  // Shape: { name, columns, rows, … } — ONE native table, as written by the
  // per-table JSON export. Without this it would fall through to the
  // single-object fallback below and become one row of nonsense.
  if (isSingleNativeTable(parsed)) {
    return [nativeTableToNormalized(parsed)];
  }

  // Shape: [{...}, {...}]  — array of objects, infer columns from union of keys
  if (Array.isArray(parsed)) {
    const rows = parsed.filter(isObject) as Array<Record<string, unknown>>;
    if (rows.length === 0) return [];
    return [{ name: fallbackName, ...inferTableFromRows(rows) }];
  }

  // Shape: {...}  — single object, treat as one row
  if (isObject(parsed)) {
    const obj = parsed as Record<string, unknown>;
    return [{ name: fallbackName, ...inferTableFromRows([obj]) }];
  }

  return [];
}

// -- v1 / legacy minniDBMax dump format --------------------------------------

interface V1Column {
  field: string;
  name?: string; // v1 calls the label "name"
  type?: string;
  isUnique?: boolean;
  isNotNull?: boolean;
}

interface V1Table {
  dataArray: unknown[][]; // positional rows aligned with `columns`
  columns: V1Column[];
  elementRect?: {
    x?: number;
    y?: number;
    width?: number;
    height?: number;
    zIndex?: number;
    minimized?: boolean;
    maximized?: boolean;
  };
  sortColumn?: number; // index into columns, or -1 for none
  sortDirection?: 'asc' | 'desc';
}

function looksLikeV1Dump(obj: Record<string, unknown>): boolean {
  for (const [k, v] of Object.entries(obj)) {
    if (!/\.table\.json$/.test(k)) continue;
    if (!isObject(v)) continue;
    const o = v as Record<string, unknown>;
    if (Array.isArray(o.dataArray) && Array.isArray(o.columns)) return true;
  }
  return false;
}

function convertV1Dump(obj: Record<string, unknown>): NormalizedTable[] {
  const out: NormalizedTable[] = [];
  for (const [key, raw] of Object.entries(obj)) {
    if (!/\.table\.json$/.test(key)) continue;
    if (!isObject(raw)) continue;
    const t = raw as unknown as V1Table;
    if (!Array.isArray(t.dataArray) || !Array.isArray(t.columns)) continue;

    const name = key.replace(/\.table\.json$/, '');
    const columns: ColumnSpec[] = t.columns.map((c) => normalizeV1Column(c));
    const fields = columns.map((c) => c.field);

    // dataArray rows are positional arrays aligned with the columns array.
    // Translate each into a field-keyed row object.
    const rows: Array<Record<string, unknown>> = t.dataArray
      .filter((r) => Array.isArray(r))
      .map((r) => {
        const obj: Record<string, unknown> = {};
        for (let i = 0; i < fields.length; i++) {
          obj[fields[i]!] = (r as unknown[])[i];
        }
        return obj;
      });

    const nt: NormalizedTable = { name, columns, rows };
    if (t.elementRect && typeof t.elementRect.x === 'number' && typeof t.elementRect.y === 'number') {
      // Only honor the saved geometry when both x/y are present. Without an
      // actual position from the dump, leave windowGeometry unset so the
      // window manager cascades the new panel instead of stacking it at 0,0.
      const r = t.elementRect as {
        x: number;
        y: number;
        width?: number;
        height?: number;
        zIndex?: number;
        minimized?: boolean;
        maximized?: boolean;
      };
      nt.windowGeometry = {
        x: r.x,
        y: r.y,
        w: r.width ?? 600,
        h: r.height ?? 400,
        z: r.zIndex ?? 100,
        minimized: !!r.minimized,
        maximized: !!r.maximized,
      };
    }
    if (typeof t.sortColumn === 'number' && t.sortColumn >= 0 && t.sortColumn < fields.length) {
      nt.sortColumn = fields[t.sortColumn]!;
      nt.sortAsc = (t.sortDirection ?? 'asc') !== 'desc';
    }
    out.push(nt);
  }
  return out;
}

function normalizeV1Column(c: V1Column): ColumnSpec {
  const field = String(c.field ?? 'col');
  const label = String(c.name ?? field);
  const t = (typeof c.type === 'string' ? c.type : 'string') as ColumnType;
  const spec: ColumnSpec = { field, label, type: t };
  if (c.isUnique) spec.unique = true;
  if (c.isNotNull) spec.notnull = true;
  return spec;
}

function isNativeTable(v: unknown): v is { name: unknown; columns: unknown[]; rows?: unknown } {
  return isObject(v) && 'name' in v && 'columns' in v && Array.isArray((v as { columns: unknown }).columns);
}

/**
 * A top-level single native table (the per-table JSON export). Stricter than
 * `isNativeTable`: `rows` must be an array too, so an unrelated document that
 * merely has `name` and `columns` still falls through to the generic shapes.
 * A "Structure Only" export has `rows: []`, which still qualifies.
 */
function isSingleNativeTable(v: unknown): v is { name: unknown; columns: unknown[]; rows: unknown[] } {
  return isNativeTable(v) && Array.isArray((v as { rows?: unknown }).rows);
}

/**
 * Reads name/columns/rows plus everything a native table entry can carry —
 * window geometry, sort, filters, label column, table info, deleted columns,
 * the read-only flag, and a live `source` or snapshot `origin` — into one
 * `NormalizedTable`.
 * Shared by the `{ tables: [...] }` dump loop and a top-level single native
 * table, so both carry the exact same fields.
 */
function nativeTableToNormalized(entry: { name: unknown; columns: unknown[]; rows?: unknown }): NormalizedTable {
  const e = entry as Record<string, unknown>;
  const geom = isObject(e.windowGeometry) ? (e.windowGeometry as WindowGeometry) : undefined;
  const sortColumn = typeof e.sortColumn === 'string' ? e.sortColumn : undefined;
  const sortAsc = typeof e.sortAsc === 'boolean' ? e.sortAsc : undefined;
  const title = typeof e.title === 'string' ? e.title : undefined;
  const filters = isObject(e.filters) ? (e.filters as Record<string, string>) : undefined;
  const labelColumn = typeof e.labelColumn === 'string' ? e.labelColumn : undefined;
  const info = isObject(e.info) ? (e.info as TableInfo) : undefined;
  const deletedColumns = Array.isArray(e.deletedColumns) ? (e.deletedColumns.filter((c) => typeof c === 'string') as string[]) : undefined;
  const readonly = e.readonly === true ? true : undefined;
  // Carry a live `source` or snapshot `origin` if the dump recorded one.
  const source = isObject(e.source) && typeof (e.source as { type?: unknown }).type === 'string' ? (e.source as unknown as TableSource) : undefined;
  const origin =
    isObject(e.origin) && typeof (e.origin as { type?: unknown }).type === 'string' && typeof (e.origin as { url?: unknown }).url === 'string' ? (e.origin as unknown as TableOrigin) : undefined;
  const rows = Array.isArray(entry.rows) ? (entry.rows.filter(isObject) as Array<Record<string, unknown>>) : [];
  return {
    name: String(entry.name),
    columns: withUndeclaredFields(entry.columns.map(normalizeColumn), rows, deletedColumns ?? []),
    rows,
    ...(title ? { title } : {}),
    ...(geom ? { windowGeometry: geom } : {}),
    ...(sortColumn ? { sortColumn, sortAsc: sortAsc ?? true } : {}),
    ...(filters ? { filters } : {}),
    ...(labelColumn ? { labelColumn } : {}),
    ...(info ? { info } : {}),
    ...(deletedColumns ? { deletedColumns } : {}),
    ...(readonly ? { readonly } : {}),
    ...(source ? { source } : {}),
    ...(origin ? { origin } : {}),
  };
}

/**
 * Add a column for every field the ROWS carry that the dump's own column list left
 * out.
 *
 * A dump is meant to be self-consistent. One from the wild is not: `bible.db.json`
 * declares `book` and no `title`, yet 368 of its 1,258 rows carry `title` and no
 * `book` — one logical field under two names, written by two generations of the
 * exporter. Those 368 rows imported blank. The value was in the row the whole time
 * with no column to show it, no header to sort by and no funnel to filter on, so it
 * was not recoverable by hand either.
 *
 * Only ADDING, and only at the end. A declared column carries width, renderer,
 * hidden and the rest, and the declared order is the user's. A field the user
 * deliberately removed is remembered in `deletedColumns` and stays removed: that
 * list is exactly what tells a deletion apart from an omission.
 */
function withUndeclaredFields(columns: ColumnSpec[], rows: Array<Record<string, unknown>>, deletedColumns: readonly string[]): ColumnSpec[] {
  const known = new Set(columns.map((c) => c.field));
  const deleted = new Set(deletedColumns);
  const extra: string[] = [];
  for (const r of rows) {
    for (const k of Object.keys(r)) {
      if (known.has(k) || deleted.has(k)) continue;
      known.add(k);
      extra.push(k);
    }
  }
  if (extra.length === 0) return columns;
  return [
    ...columns,
    ...extra.map(
      (field): ColumnSpec => ({
        field,
        label: field,
        type: inferTypeFromValues(rows.map((r) => r[field])),
      }),
    ),
  ];
}

function normalizeColumn(c: unknown): ColumnSpec {
  if (!isObject(c)) return { field: 'col', label: 'Col', type: 'string' };
  const o = c as Record<string, unknown>;
  const field = String(o.field ?? 'col');
  let type: ColumnType = (typeof o.type === 'string' ? o.type : 'string') as ColumnType;
  let renderer = typeof o.renderer === 'string' ? o.renderer : undefined;
  // Pre-v3 dumps stored color/image as column types rather than renderers.
  // Rewrite on the way in so newly-imported tables match today's shape.
  if ((type as string) === 'color' || (type as string) === 'image') {
    renderer = renderer ?? (type as string);
    type = 'string';
  }
  const spec: ColumnSpec = {
    field,
    label: String(o.label ?? field),
    type,
  };
  if (renderer) spec.renderer = renderer;
  if (typeof o.script === 'string') spec.script = o.script;
  if (o.scriptActive === false) spec.scriptActive = false;
  if (typeof o.validate === 'string') spec.validate = o.validate;
  if (o.validateActive === false) spec.validateActive = false;
  if (o.default !== undefined) spec.default = o.default;
  if (typeof o.max === 'number') spec.max = o.max;
  if (typeof o.width === 'number') spec.width = o.width;
  if (typeof o.description === 'string') spec.description = o.description;
  if (typeof o.units === 'string') spec.units = o.units;
  if (o.unique === true) spec.unique = true;
  if (o.notnull === true) spec.notnull = true;
  // A hidden column came back visible: dump-export writes the whole column, but
  // this reader only copied field/label/type/renderer/script, so every flag the
  // user had set was silently dropped on the way in.
  if (o.hidden === true) spec.hidden = true;
  // Only an explicit `false` means anything for these two — absent is the
  // permissive default (see ColumnSpec).
  if (o.sortable === false) spec.sortable = false;
  if (o.filterable === false) spec.filterable = false;
  // Preserve the read-only flag so a dumped Projection's computed / joined
  // columns come back non-editable.
  if (o.readonly === true) spec.readonly = true;
  return spec;
}

// -- Column inference for array-of-objects ------------------------------------

function inferTableFromRows(rows: Array<Record<string, unknown>>): {
  columns: ColumnSpec[];
  rows: Array<Record<string, unknown>>;
} {
  const fields = new Set<string>();
  for (const r of rows) for (const k of Object.keys(r)) fields.add(k);
  const order = Array.from(fields);

  const columns: ColumnSpec[] = order.map((field) => ({
    field,
    label: field,
    type: inferTypeFromValues(rows.map((r) => r[field])),
  }));

  return { columns, rows };
}

/**
 * JSON arrives already typed, so the inferrer trusts the JS types and only reads
 * spellings for the date shapes. Big integers are guarded earlier, at parse time
 * (`quoteBigIntegers`) — by the time `JSON.parse` has run the precision is gone.
 */
function inferTypeFromValues(values: unknown[]): ColumnType {
  return inferColumnType(values);
}

// -- helpers ------------------------------------------------------------------

function isObject(v: unknown): v is object {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function filesFrom(event: DragEvent): File[] {
  const dt = event.dataTransfer;
  if (!dt) return [];
  if (dt.files && dt.files.length > 0) return Array.from(dt.files);
  if (dt.items) {
    const out: File[] = [];
    for (const item of Array.from(dt.items)) {
      if (item.kind === 'file') {
        const f = item.getAsFile();
        if (f) out.push(f);
      }
    }
    return out;
  }
  return [];
}

function isJson(file: File): boolean {
  if (/\.json$/i.test(file.name)) return true;
  if (file.type === 'application/json') return true;
  return false;
}
